import { useState, useEffect } from 'react';
import { 
  Search, FileText, CheckCircle, XCircle, Clock,
  Loader2, AlertCircle, User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getStatusConfig(status) {
  if (status === 'Pendiente') return { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', Icon: Clock };
  if (status === 'En Revisión') return { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', Icon: AlertCircle };
  if (status === 'Aprobado') return { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20', Icon: CheckCircle };
  if (status === 'Rechazado') return { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', Icon: XCircle };
  return { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', Icon: Clock };
}

export function AdminDocuments() {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Pendiente');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({ status: '', observaciones: '' });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, [statusFilter]);

  async function fetchDocuments() {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/admin/professionals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const professionals = await response.json();
        const allDocs = [];
        
        for (let i = 0; i < professionals.length; i++) {
          const prof = professionals[i];
          if (prof.documentos && prof.documentos.length > 0) {
            for (let j = 0; j < prof.documentos.length; j++) {
              const doc = prof.documentos[j];
              if (!statusFilter || statusFilter === 'all' || doc.status === statusFilter) {
                allDocs.push({ document: doc, professional: prof });
              }
            }
          }
        }
        
        setDocuments(allDocs);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleReview(document, professional) {
    setSelectedDocument(document);
    setSelectedProfessional(professional);
    setReviewForm({ status: document.status, observaciones: document.observaciones || '' });
    setIsReviewModalOpen(true);
  }

  async function handleSaveReview() {
    if (!selectedDocument || !selectedProfessional) return;
    setIsSaving(true);

    try {
      const response = await fetch(
        `${API}/admin/document/${selectedProfessional.id}/${selectedDocument.id}/review`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: reviewForm.status,
            observaciones: reviewForm.observaciones
          })
        }
      );

      if (response.ok) {
        setIsReviewModalOpen(false);
        fetchDocuments();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error al actualizar');
      }
    } catch (error) {
      console.error('Error updating document:', error);
    } finally {
      setIsSaving(false);
    }
  }

  const filteredDocuments = documents.filter(function(item) {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    if (item.document.tipo && item.document.tipo.toLowerCase().includes(searchLower)) return true;
    if (item.professional.nombre && item.professional.nombre.toLowerCase().includes(searchLower)) return true;
    return false;
  });

  function renderDocumentCard(item, index) {
    const doc = item.document;
    const prof = item.professional;
    const config = getStatusConfig(doc.status);
    const StatusIcon = config.Icon;

    return (
      <div key={doc.id || index} className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-6 hover:border-[#3a3a3a] transition-colors">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg ${config.bg} flex items-center justify-center`}>
              <FileText className={`w-6 h-6 ${config.text}`} />
            </div>
            <div>
              <h3 className="text-white font-medium">{doc.tipo}</h3>
              <p className="text-sm text-gray-500">{doc.nombre_archivo}</p>
            </div>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
            <StatusIcon className="w-3 h-3 inline mr-1" />{doc.status}
          </span>
        </div>
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <User className="w-4 h-4 text-gray-500" />
            <span className="text-gray-400">{prof.nombre}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-gray-400">Subido: {doc.fecha_subida ? new Date(doc.fecha_subida).toLocaleDateString('es-MX') : '-'}</span>
          </div>
        </div>
        {(doc.status === 'Pendiente' || doc.status === 'En Revisión') && (
          <Button size="sm" onClick={() => handleReview(doc, prof)} className="bg-white text-black hover:bg-gray-200">
            Revisar
          </Button>
        )}
      </div>
    );
  }

  const cards = [];
  for (let i = 0; i < filteredDocuments.length; i++) {
    cards.push(renderDocumentCard(filteredDocuments[i], i));
  }

  return (
    <div className="p-8" data-testid="admin-documents">
      <div className="mb-8">
        <h1 className="font-manrope font-bold text-2xl text-white mb-2">Revisión de Documentos</h1>
        <p className="text-gray-400">Aprueba o rechaza los documentos subidos por los profesionales</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input placeholder="Buscar por tipo o profesional..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-[#111111] border-[#2a2a2a] text-white" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
          <SelectTrigger className="w-full md:w-48 bg-[#111111] border-[#2a2a2a] text-white">
            <SelectValue placeholder="Filtrar por estatus" />
          </SelectTrigger>
          <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="Pendiente">Pendiente</SelectItem>
            <SelectItem value="En Revisión">En Revisión</SelectItem>
            <SelectItem value="Aprobado">Aprobado</SelectItem>
            <SelectItem value="Rechazado">Rechazado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      ) : cards.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{cards}</div>
      ) : (
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No se encontraron documentos</p>
        </div>
      )}

      <Dialog open={isReviewModalOpen} onOpenChange={setIsReviewModalOpen}>
        <DialogContent className="bg-[#111111] border-[#2a2a2a] text-white">
          <DialogHeader><DialogTitle className="text-white">Revisar Documento</DialogTitle></DialogHeader>
          {selectedDocument && selectedProfessional && (
            <div className="space-y-6">
              <div className="bg-[#0a0a0a] rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-1">Documento</p>
                <p className="text-white font-medium">{selectedDocument.tipo}</p>
              </div>
              <div className="bg-[#0a0a0a] rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-1">Profesional</p>
                <p className="text-white">{selectedProfessional.nombre}</p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Decisión</Label>
                  <Select value={reviewForm.status} onValueChange={(v) => setReviewForm({ ...reviewForm, status: v })}>
                    <SelectTrigger className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                      <SelectItem value="Pendiente">Pendiente</SelectItem>
                      <SelectItem value="En Revisión">En Revisión</SelectItem>
                      <SelectItem value="Aprobado">Aprobar</SelectItem>
                      <SelectItem value="Rechazado">Rechazar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Observaciones</Label>
                  <Textarea value={reviewForm.observaciones} onChange={(e) => setReviewForm({ ...reviewForm, observaciones: e.target.value })} className="mt-1 bg-[#0a0a0a] border-[#2a2a2a] text-white" placeholder="Razón de la decisión..." rows={3} />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setIsReviewModalOpen(false)} className="border-[#2a2a2a] text-gray-400">Cancelar</Button>
                <Button onClick={handleSaveReview} disabled={isSaving} className="bg-white text-black hover:bg-gray-200">
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Guardar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
